import Dh from '@/components/Dh';
import { PLAN_ICONS, type PlanContent, type PlanPrice } from '@/data/setupPricing';

/* One selectable plan card in the incorporate (/setup-plan) and operate (/operate-plan)
   flows. Content is admin-editable via setupPricing/current.{setupPlans,operatePlans};
   the price comes from the shared setupPricing/current.plans[id] map. */

const RadioCircle = ({ selected }: { selected: boolean }) => (
  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-amber-500' : 'border-gray-400'}`}>
    {selected && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
  </div>
);

export default function PlanCard({
  plan,
  price,
  selected,
  onSelect,
}: {
  plan: PlanContent;
  price: PlanPrice | undefined;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = PLAN_ICONS[plan.icon] ?? PLAN_ICONS.tag;
  return (
    <div
      onClick={onSelect}
      className="bg-white rounded-xl overflow-hidden cursor-pointer flex flex-col transition-all duration-200"
      style={{
        border: plan.mostPopular ? '2px solid #F59E0B' : selected ? '2px solid #E5E7EB' : '1px solid #E5E7EB',
        boxShadow: selected ? '0 4px 20px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {plan.mostPopular && (
        <div className="bg-amber-400 text-center py-2">
          <span className="text-sm font-semibold text-gray-900">Most Popular</span>
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        <Icon className="w-7 h-7 text-gray-700 mb-4" strokeWidth={1.5} />

        <div className="mb-3">
          <span className="text-xl font-bold text-gray-900">{plan.title}</span>
          {plan.titleNote && <span className="text-xs text-gray-500 ml-1.5">{plan.titleNote}</span>}
        </div>

        {plan.description && <p className="text-gray-600 text-sm mb-4 leading-relaxed">{plan.description}</p>}

        {plan.featuresLabel && <p className="text-sm font-bold text-gray-900 mb-2">{plan.featuresLabel}</p>}
        <ul className="space-y-1.5 flex-1 mb-4">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-gray-400 mt-0.5 shrink-0">·</span>
              <span className="text-sm text-gray-600 leading-snug">{f}</span>
            </li>
          ))}
        </ul>

        {plan.badge && (
          <p className="text-sm font-semibold mb-3" style={{ color: '#D97706' }}>{plan.badge}</p>
        )}

        <div className="flex items-end justify-between pt-3 border-t border-gray-100 mt-auto">
          <div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-base font-bold text-gray-900 inline-flex items-baseline"><Dh em="0.8" />{(price?.priceYr ?? 0).toLocaleString()}/yr</span>
              {plan.discountLabel && (
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">{plan.discountLabel}</span>
              )}
            </div>
            {price?.priceMo != null && (
              <p className="text-base font-semibold text-gray-900">{price.priceMo.toLocaleString()}/monthly</p>
            )}
            {plan.priceNote && <p className="text-xs text-gray-500">{plan.priceNote}</p>}
          </div>
          <RadioCircle selected={selected} />
        </div>
      </div>
    </div>
  );
}
